/*!
 * Hazemor — motion & interaction layer for Horizon.
 * Plain JS, no dependencies. Every effect degrades to static content without JS
 * and is disabled under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var d = document;
  var w = window;
  var html = d.documentElement;
  var reduceMQ = w.matchMedia('(prefers-reduced-motion: reduce)');
  var fineMQ = w.matchMedia('(hover: hover) and (pointer: fine)');
  var deskMQ = w.matchMedia('(min-width: 990px)');

  html.classList.add('hz-js');

  var reduced = function () { return reduceMQ.matches; };
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); };

  /* ------------------------------------------------------------ scroll plumbing
     Horizon scrolls `.page-wrapper` on desktop and the document on mobile. */
  var wrapper = null;
  var getWrapper = function () {
    if (!wrapper || !wrapper.isConnected) wrapper = d.querySelector('.page-wrapper');
    return wrapper;
  };
  var scrollY = function () {
    var pw = getWrapper();
    return (pw && pw.scrollTop) || w.pageYOffset || html.scrollTop || 0;
  };
  var scroller = function () {
    var pw = getWrapper();
    if (pw && deskMQ.matches && pw.scrollHeight > pw.clientHeight + 2) return pw;
    return null;
  };
  var scrollToY = function (y, smooth) {
    var s = scroller();
    var opts = { top: y, behavior: smooth && !reduced() ? 'smooth' : 'auto' };
    if (s) s.scrollTo(opts);
    else w.scrollTo(opts);
  };

  var subs = [];
  var ticking = false;
  var vh = w.innerHeight;
  var run = function () {
    ticking = false;
    for (var i = 0; i < subs.length; i++) subs[i]();
  };
  var request = function () {
    if (!ticking) {
      ticking = true;
      w.requestAnimationFrame(run);
    }
  };
  var onScroll = function (fn) {
    subs.push(fn);
    request();
  };
  var bound = false;
  var bindScroll = function () {
    if (bound) return;
    bound = true;
    w.addEventListener('scroll', request, { passive: true });
    var pw = getWrapper();
    if (pw) pw.addEventListener('scroll', request, { passive: true });
    w.addEventListener('resize', function () {
      vh = w.innerHeight;
      request();
    }, { passive: true });
  };

  /* ------------------------------------------------------------ split text */
  function splitWords(el) {
    if (el.dataset.hzSplitDone) return;
    el.dataset.hzSplitDone = '1';
    var walk = function (node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      kids.forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          var frag = d.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(d.createTextNode(' '));
            } else {
              var outer = d.createElement('span');
              outer.className = 'hz-w';
              var inner = d.createElement('span');
              inner.className = 'hz-w__i';
              inner.textContent = part;
              outer.appendChild(inner);
              frag.appendChild(outer);
            }
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && !/^(BR|SVG|IMG|PICTURE)$/i.test(child.nodeName) && !child.classList.contains('hz-capsule')) {
          walk(child);
        }
      });
    };
    walk(el);
    measureLines(el);
  }

  function measureLines(el) {
    var words = $$('.hz-w', el);
    var line = -1;
    var lastTop = null;
    var wi = 0;
    words.forEach(function (word) {
      var top = Math.round(word.offsetTop);
      if (lastTop === null || Math.abs(top - lastTop) > 4) {
        line += 1;
        lastTop = top;
        wi = 0;
      }
      word.style.setProperty('--hz-line', line);
      word.style.setProperty('--hz-wi', wi);
      wi += 1;
    });
  }

  /* ------------------------------------------------------------ reveal on view */
  var revealIO = null;
  function getRevealIO() {
    if (revealIO) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-in');
        revealIO.unobserve(el);
        if (el.hasAttribute('data-hz-count')) countUp(el);
        el.dispatchEvent(new CustomEvent('hz:in', { bubbles: true }));
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    return revealIO;
  }

  function initReveal(root) {
    $$('[data-hz-stagger]', root).forEach(function (group) {
      var step = parseInt(group.getAttribute('data-hz-stagger'), 10) || 0;
      $$('[data-hz-reveal], [data-hz-unveil]', group).forEach(function (el, i) {
        el.style.setProperty('--hz-i', i + step);
      });
    });
    $$('[data-hz-split]', root).forEach(splitWords);
    var targets = $$('[data-hz-reveal], [data-hz-split], [data-hz-unveil], [data-hz-count]', root);
    if (reduced()) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = getRevealIO();
    targets.forEach(function (el) {
      if (!el.classList.contains('is-in')) io.observe(el);
    });
  }

  var resizeTimer = null;
  w.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      $$('[data-hz-split]').forEach(measureLines);
    }, 180);
  });

  /* ------------------------------------------------------------ count up */
  function countUp(el) {
    var to = parseFloat(el.getAttribute('data-hz-count'));
    if (isNaN(to)) return;
    var dec = parseInt(el.getAttribute('data-decimals') || '0', 10);
    if (reduced()) {
      el.textContent = to.toFixed(dec);
      return;
    }
    var from = parseFloat(el.getAttribute('data-from') || '0');
    var dur = 1700;
    var t0 = null;
    var tick = function (t) {
      if (t0 === null) t0 = t;
      var p = clamp((t - t0) / dur, 0, 1);
      var e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = lerp(from, to, e).toFixed(dec);
      if (p < 1) w.requestAnimationFrame(tick);
    };
    el.textContent = from.toFixed(dec);
    w.requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------ parallax */
  var parallaxEls = [];
  function initParallax(root) {
    $$('[data-hz-parallax]', root).forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }
  onScroll(function () {
    if (reduced()) return;
    for (var i = 0; i < parallaxEls.length; i++) {
      var el = parallaxEls[i];
      if (!el.isConnected) continue;
      var host = el.parentElement || el;
      var r = host.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      var speed = parseFloat(el.getAttribute('data-hz-parallax')) || 0.12;
      var center = r.top + r.height / 2 - vh / 2;
      var y = -center * speed;
      el.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
    }
  });

  /* ------------------------------------------------------------ scrubbed statement */
  var scrubEls = [];
  function initScrub(root) {
    $$('[data-hz-scrub]', root).forEach(function (el) {
      if (el.dataset.hzScrubDone) return;
      el.dataset.hzScrubDone = '1';
      splitWords(el);
      scrubEls.push({ el: el, words: $$('.hz-w', el) });
    });
  }
  onScroll(function () {
    for (var i = 0; i < scrubEls.length; i++) {
      var item = scrubEls[i];
      if (!item.el.isConnected) continue;
      var r = item.el.getBoundingClientRect();
      if (reduced()) {
        item.words.forEach(function (wd) { wd.classList.add('on'); });
        continue;
      }
      if (r.bottom < 0 || r.top > vh) continue;
      var start = vh * 0.82;
      var end = vh * 0.38;
      var p = clamp((start - r.top) / (r.height + start - end), 0, 1);
      var n = Math.round(p * item.words.length * 1.08);
      for (var j = 0; j < item.words.length; j++) {
        item.words[j].classList.toggle('on', j < n);
      }
    }
  });

  /* ------------------------------------------------------------ pinned steps */
  var stepsEls = [];
  function initSteps(root) {
    $$('[data-hz-steps]', root).forEach(function (sec) {
      if (sec.dataset.hzStepsDone) return;
      sec.dataset.hzStepsDone = '1';
      var track = sec.querySelector('.hz-steps__track');
      var n = parseInt(sec.getAttribute('data-count'), 10) || 1;
      var state = { sec: sec, track: track, n: n, active: -1 };
      stepsEls.push(state);
      $$('[data-hz-goto]', sec).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var i = parseInt(btn.getAttribute('data-hz-goto'), 10) || 0;
          if (!deskMQ.matches) return;
          var r = track.getBoundingClientRect();
          var span = r.height - vh;
          var target = scrollY() + r.top + span * ((i + 0.35) / n);
          scrollToY(target, true);
        });
      });
    });
  }
  onScroll(function () {
    for (var i = 0; i < stepsEls.length; i++) {
      var s = stepsEls[i];
      if (!s.sec.isConnected) continue;
      if (!deskMQ.matches) {
        if (s.active !== -2) {
          s.active = -2;
          s.sec.removeAttribute('data-active');
        }
        continue;
      }
      var r = s.track.getBoundingClientRect();
      var span = Math.max(1, r.height - vh);
      var p = clamp(-r.top / span, 0, 1);
      s.sec.style.setProperty('--hz-p', p.toFixed(4));
      var idx = Math.min(s.n - 1, Math.floor(p * s.n * 0.9999));
      if (idx !== s.active) {
        s.active = idx;
        s.sec.setAttribute('data-active', idx);
        $$('[data-step]', s.sec).forEach(function (el) {
          var k = parseInt(el.getAttribute('data-step'), 10);
          el.classList.toggle('is-active', k === idx);
          el.classList.toggle('is-past', k < idx);
        });
        $$('[data-hz-goto]', s.sec).forEach(function (btn) {
          var k = parseInt(btn.getAttribute('data-hz-goto'), 10);
          if (k === idx) btn.setAttribute('aria-current', 'step');
          else btn.removeAttribute('aria-current');
        });
      }
    }
  });

  /* ------------------------------------------------------------ drag gallery */
  function initDrag(root) {
    $$('[data-hz-drag]', root).forEach(function (wrap) {
      if (wrap.dataset.hzDragDone) return;
      wrap.dataset.hzDragDone = '1';
      var track = wrap.querySelector('[data-hz-track]');
      if (!track) return;
      var bar = wrap.querySelector('[data-hz-progress]');
      var prev = wrap.querySelector('[data-hz-prev]');
      var next = wrap.querySelector('[data-hz-next]');
      var update = function () {
        var max = track.scrollWidth - track.clientWidth;
        var p = max > 0 ? track.scrollLeft / max : 0;
        if (bar) bar.style.setProperty('--hz-p', p.toFixed(4));
        if (prev) prev.disabled = track.scrollLeft < 4;
        if (next) next.disabled = track.scrollLeft > max - 4;
      };
      track.addEventListener('scroll', update, { passive: true });
      w.addEventListener('resize', update, { passive: true });
      update();
      var stepBy = function (dir) {
        var item = track.querySelector('[data-hz-item]');
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
        var dx = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * dx, behavior: reduced() ? 'auto' : 'smooth' });
      };
      if (prev) prev.addEventListener('click', function () { stepBy(-1); });
      if (next) next.addEventListener('click', function () { stepBy(1); });

      // Mouse drag with momentum (touch uses native scrolling)
      var down = false;
      var startX = 0;
      var startLeft = 0;
      var moved = 0;
      var lastX = 0;
      var lastT = 0;
      var vel = 0;
      var raf = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        moved = 0;
        startX = lastX = e.clientX;
        lastT = performance.now();
        startLeft = track.scrollLeft;
        vel = 0;
        cancelAnimationFrame(raf);
        track.classList.add('is-dragging');
      });
      w.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        track.scrollLeft = startLeft - dx;
        var now = performance.now();
        var dt = Math.max(1, now - lastT);
        vel = (e.clientX - lastX) / dt;
        lastX = e.clientX;
        lastT = now;
      });
      var release = function () {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
        if (reduced()) return;
        var v = -vel * 16;
        var glide = function () {
          v *= 0.94;
          track.scrollLeft += v;
          if (Math.abs(v) > 0.4) raf = requestAnimationFrame(glide);
        };
        raf = requestAnimationFrame(glide);
      };
      w.addEventListener('pointerup', release);
      w.addEventListener('pointercancel', release);
      track.addEventListener('click', function (e) {
        if (moved > 6) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  /* ------------------------------------------------------------ film (lite YouTube / Vimeo, uploaded player) */
  function initFilm(root) {
    $$('[data-hz-film]', root).forEach(function (frame) {
      if (frame.dataset.hzFilmDone) return;
      frame.dataset.hzFilmDone = '1';
      var btn = frame.querySelector('[data-hz-play]');
      if (!btn) return;
      var provider = frame.getAttribute('data-provider') || 'youtube';
      var warmed = false;
      var warm = function () {
        if (warmed) return;
        warmed = true;
        var hosts = provider === 'vimeo' ? ['https://player.vimeo.com', 'https://i.vimeocdn.com'] : ['https://www.youtube-nocookie.com', 'https://i.ytimg.com'];
        hosts.forEach(function (href) {
          var l = d.createElement('link');
          l.rel = 'preconnect';
          l.href = href;
          d.head.appendChild(l);
        });
      };
      frame.addEventListener('pointerenter', warm, { once: true });
      var play = function () {
        var id = frame.getAttribute('data-yt');
        if (!id || frame.classList.contains('is-playing')) return;
        var iframe = d.createElement('iframe');
        iframe.src = provider === 'vimeo'
          ? 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1&dnt=1'
          : 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        iframe.title = frame.getAttribute('data-title') || 'Video';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
        iframe.allowFullscreen = true;
        iframe.className = 'hz-film__iframe';
        frame.appendChild(iframe);
        frame.classList.add('is-playing');
        iframe.focus();
      };
      btn.addEventListener('click', function (e) { e.stopPropagation(); play(); });
      frame.addEventListener('click', play);
    });
    $$('[data-hz-player]', root).forEach(function (frame) {
      if (frame.dataset.hzPlayerDone) return;
      frame.dataset.hzPlayerDone = '1';
      var video = frame.querySelector('video');
      var btn = frame.querySelector('[data-hz-play]');
      if (!video || !btn) return;
      var start = function (e) {
        if (e) e.stopPropagation();
        if (frame.classList.contains('is-playing')) return;
        frame.classList.add('is-playing');
        video.setAttribute('preload', 'auto');
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
        video.focus();
      };
      btn.addEventListener('click', start);
      frame.addEventListener('click', function (e) {
        if (e.target === video) return;
        start(e);
      });
    });
    if (!fineMQ.matches) return;
    $$('[data-hz-follow]', root).forEach(function (frame) {
      if (frame.dataset.hzFollowDone) return;
      frame.dataset.hzFollowDone = '1';
      var btn = frame.querySelector('.hz-film__play');
      if (!btn) return;
      frame.addEventListener('pointermove', function (e) {
        if (reduced() || frame.classList.contains('is-playing')) return;
        var r = frame.getBoundingClientRect();
        var x = clamp((e.clientX - r.left) / r.width, 0.12, 0.88) * 100;
        var y = clamp((e.clientY - r.top) / r.height, 0.16, 0.84) * 100;
        frame.setAttribute('data-follow-active', '');
        btn.style.setProperty('--fx', x.toFixed(2) + '%');
        btn.style.setProperty('--fy', y.toFixed(2) + '%');
      });
      frame.addEventListener('pointerleave', function () {
        frame.removeAttribute('data-follow-active');
        btn.style.setProperty('--fx', '50%');
        btn.style.setProperty('--fy', '50%');
      });
    });
  }

  /* ------------------------------------------------------------ accordions */
  function initAccordions(root) {
    $$('details.hz-acc__item', root).forEach(function (det) {
      if (det.dataset.hzAccDone) return;
      det.dataset.hzAccDone = '1';
      var summary = det.querySelector('summary');
      var panel = det.querySelector('.hz-acc__panel');
      if (!summary || !panel) return;
      var anim = null;
      summary.addEventListener('click', function (e) {
        if (reduced() || !panel.animate) return;
        e.preventDefault();
        if (anim) anim.cancel();
        if (det.open) {
          var h0 = panel.offsetHeight;
          anim = panel.animate([{ height: h0 + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
          anim.onfinish = function () { det.open = false; anim = null; };
        } else {
          det.open = true;
          var h1 = panel.offsetHeight;
          anim = panel.animate([{ height: '0px', opacity: 0 }, { height: h1 + 'px', opacity: 1 }], { duration: 560, easing: 'cubic-bezier(.22,1,.36,1)' });
          anim.onfinish = function () { anim = null; };
        }
      });
    });
  }

  /* ------------------------------------------------------------ magnetic buttons & glow */
  function initPointer(root) {
    if (!fineMQ.matches) return;
    $$('.hz-btn', root).forEach(function (btn) {
      if (btn.dataset.hzMagDone) return;
      btn.dataset.hzMagDone = '1';
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width;
        btn.style.setProperty('--hz-mx', (x * 100).toFixed(1) + '%');
        if (reduced() || !btn.hasAttribute('data-hz-magnetic')) return;
        var dx = (e.clientX - (r.left + r.width / 2)) * 0.16;
        var dy = (e.clientY - (r.top + r.height / 2)) * 0.28;
        btn.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.transform = '';
      });
    });
    $$('[data-hz-glow]', root).forEach(function (el) {
      if (el.dataset.hzGlowDone) return;
      el.dataset.hzGlowDone = '1';
      var raf = 0;
      var tx = 50;
      var ty = 40;
      var cx = 50;
      var cy = 40;
      var loop = function () {
        cx = lerp(cx, tx, 0.08);
        cy = lerp(cy, ty, 0.08);
        el.style.setProperty('--gx', cx.toFixed(2) + '%');
        el.style.setProperty('--gy', cy.toFixed(2) + '%');
        if (Math.abs(cx - tx) > 0.05 || Math.abs(cy - ty) > 0.05) raf = requestAnimationFrame(loop);
        else raf = 0;
      };
      el.addEventListener('pointermove', function (e) {
        if (reduced()) return;
        var r = el.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width) * 100;
        ty = ((e.clientY - r.top) / r.height) * 100;
        if (!raf) raf = requestAnimationFrame(loop);
      });
    });
  }

  /* ------------------------------------------------------------ hero progress */
  var heroEls = [];
  function initHero(root) {
    $$('[data-hz-hero]', root).forEach(function (el) {
      if (heroEls.indexOf(el) === -1) heroEls.push(el);
    });
  }
  onScroll(function () {
    for (var i = 0; i < heroEls.length; i++) {
      var el = heroEls[i];
      if (!el.isConnected) continue;
      var r = el.getBoundingClientRect();
      if (r.bottom < 0) {
        el.style.setProperty('--p', '1');
        continue;
      }
      var p = reduced() ? 0 : clamp(-r.top / Math.max(1, r.height), 0, 1);
      el.style.setProperty('--p', p.toFixed(4));
    }
  });

  /* ------------------------------------------------------------ buy gallery dots */
  function initBuyGallery(root) {
    $$('[data-hz-buy-gallery]', root).forEach(function (g) {
      if (g.dataset.hzBgDone) return;
      g.dataset.hzBgDone = '1';
      var track = g.querySelector('[data-hz-buy-track]');
      var dots = $$('.hz-buy__dots span', g);
      if (!track || !dots.length) return;
      track.addEventListener('scroll', function () {
        var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        dots.forEach(function (dot, k) { dot.classList.toggle('is-on', k === i); });
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------ header */
  var header = null;
  function initHeader() {
    header = d.querySelector('.hz-header');
    if (!header || header.dataset.hzHeaderDone) return;
    header.dataset.hzHeaderDone = '1';
    var bar = header.querySelector('.hz-header__bar');
    var setHeights = function () {
      var total = header.offsetHeight;
      var barH = bar ? bar.offsetHeight : total;
      html.style.setProperty('--hz-header-total', total + 'px');
      html.style.setProperty('--hz-header-h', barH + 'px');
      d.body.style.setProperty('--header-height', barH + 'px');
      d.body.style.setProperty('--header-group-height', total + 'px');
      var ann = header.querySelector('.hz-header__ann');
      html.style.setProperty('--hz-ann-h', (ann ? ann.offsetHeight : 0) + 'px');
    };
    setHeights();
    if ('ResizeObserver' in w) new ResizeObserver(setHeights).observe(header);

    var lastY = scrollY();
    var hidden = false;
    onScroll(function () {
      var y = scrollY();
      header.classList.toggle('is-scrolled', y > 24);
      var menuOpen = header.classList.contains('is-menu-open');
      var dy = y - lastY;
      if (!menuOpen && y > 420 && dy > 6 && !hidden) {
        hidden = true;
        header.classList.add('is-hidden');
        html.classList.add('hz-header-hidden');
      } else if ((dy < -6 || y < 200) && hidden) {
        hidden = false;
        header.classList.remove('is-hidden');
        html.classList.remove('hz-header-hidden');
      }
      html.classList.toggle('hz-header-scrolled', y > 24);
      if (Math.abs(dy) > 6) lastY = y;
    });
    header.addEventListener('focusin', function () {
      hidden = false;
      header.classList.remove('is-hidden');
      html.classList.remove('hz-header-hidden');
    });

    // Mobile menu
    var menu = header.querySelector('[data-hz-menu]');
    var openers = $$('[data-hz-menu-open]', header);
    var closers = $$('[data-hz-menu-close]', header);
    var openMenu = function () {
      if (!menu) return;
      header.classList.add('is-menu-open');
      if (menu.showModal) menu.showModal();
      else menu.setAttribute('open', '');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      html.classList.add('hz-lock');
    };
    var closeMenu = function () {
      if (!menu) return;
      header.classList.remove('is-menu-open');
      menu.classList.add('is-closing');
      var finish = function () {
        menu.classList.remove('is-closing');
        if (menu.close) menu.close();
        else menu.removeAttribute('open');
      };
      if (reduced()) finish();
      else setTimeout(finish, 380);
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      html.classList.remove('hz-lock');
    };
    openers.forEach(function (b) { b.addEventListener('click', openMenu); });
    closers.forEach(function (b) { b.addEventListener('click', closeMenu); });
    if (menu) {
      menu.addEventListener('cancel', function (e) {
        e.preventDefault();
        closeMenu();
      });
      $$('a', menu).forEach(function (a) { a.addEventListener('click', closeMenu); });
    }

    // Cart opens Horizon's drawer
    $$('[data-hz-cart]', header).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var drawer = d.getElementById('cart-drawer');
        if (drawer && typeof drawer.toggle === 'function') {
          e.preventDefault();
          drawer.toggle();
        }
      });
    });
  }

  /* On desktop the drawer overlays the page (see hz-base.css); a click on the dimmed page closes it. */
  var scrimBound = false;
  function initDrawerScrim() {
    if (scrimBound) return;
    scrimBound = true;
    d.addEventListener('click', function (e) {
      var pw = getWrapper();
      if (!pw || e.target !== pw || !pw.classList.contains('page-wrapper--drawer-open')) return;
      var drawer = d.getElementById('cart-drawer');
      if (drawer && drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
    });
  }

  /* ------------------------------------------------------------ sticky purchase bar */
  function initStickyBar() {
    $$('[data-hz-stickybar]').forEach(function (bar) {
      if (bar.dataset.hzBarDone) return;
      bar.dataset.hzBarDone = '1';
      var target = d.getElementById(bar.getAttribute('data-target'));
      var buyVisible = true;
      var footVisible = false;
      if (target) {
        new IntersectionObserver(function (es) {
          es.forEach(function (e) { buyVisible = e.isIntersecting; });
          request();
        }, { threshold: 0 }).observe(target);
      }
      var foot = d.querySelector('.hz-footer');
      if (foot) {
        new IntersectionObserver(function (es) {
          es.forEach(function (e) { footVisible = e.isIntersecting; });
          request();
        }).observe(foot);
      }
      onScroll(function () {
        var show = scrollY() > vh * 0.9 && !buyVisible && !footVisible;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if ('inert' in bar) bar.inert = !show;
      });
      var go = bar.querySelector('[data-hz-bar-buy]');
      if (go && target) {
        go.addEventListener('click', function (e) {
          var form = target.querySelector('form[data-type="add-to-cart-form"]');
          var addBtn = form && form.querySelector('[type="submit"]:not([disabled])');
          if (addBtn) {
            e.preventDefault();
            addBtn.click();
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------ email signup */
  function initSignup(root) {
    $$('form[data-hz-signup]', root).forEach(function (form) {
      if (form.dataset.hzSignupDone) return;
      form.dataset.hzSignupDone = '1';
      var msg = form.querySelector('[data-hz-signup-msg]');
      var input = form.querySelector('input[type="email"]');
      var btn = form.querySelector('[type="submit"]');
      var say = function (text, kind) {
        if (!msg) return;
        msg.hidden = false;
        msg.textContent = text;
        msg.className = 'hz-signup__msg ' + (kind === 'ok' ? 'is-ok' : 'is-err');
      };
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = input ? input.value.trim() : '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          say(form.getAttribute('data-msg-invalid') || 'Please enter a valid email address.', 'err');
          if (input) input.focus();
          return;
        }
        if (btn) btn.disabled = true;
        form.classList.add('is-busy');
        var company = form.getAttribute('data-klaviyo-company');
        var list = form.getAttribute('data-klaviyo-list');
        var source = form.getAttribute('data-source') || 'Website';
        if (company && list) {
          var body = {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: source,
                profile: { data: { type: 'profile', attributes: {
                  email: email,
                  properties: { tags: (form.getAttribute('data-tags') || 'subscriber').split(/\s*,\s*/) },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                } } }
              },
              relationships: { list: { data: { type: 'list', id: list } } }
            }
          };
          fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(company), {
            method: 'POST',
            headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-04-15' },
            body: JSON.stringify(body),
            keepalive: true
          }).catch(function () {});
        }
        var data = new URLSearchParams(new FormData(form));
        fetch(form.getAttribute('action') || '/contact', {
          method: 'POST',
          body: data,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).then(function (r) {
          if (r.ok || r.status === 422 || r.redirected) {
            form.classList.add('is-done');
            say(form.getAttribute('data-msg-ok') || 'You’re on the list.', 'ok');
            try {
              if (w.Shopify && w.Shopify.analytics && w.Shopify.analytics.publish) {
                w.Shopify.analytics.publish('hazemor_email_signup', { source: source });
              }
            } catch (err) {}
            w.dispatchEvent(new CustomEvent('hz:signup', { detail: { source: source } }));
          } else {
            say('Something went wrong. Please try again.', 'err');
          }
        }).catch(function () {
          say('Network error. Please try again.', 'err');
        }).then(function () {
          if (btn) btn.disabled = false;
          form.classList.remove('is-busy');
        });
      });
    });
  }

  /* ------------------------------------------------------------ misc */
  function initMisc(root) {
    $$('[data-hz-year]', root).forEach(function (el) { el.textContent = String(new Date().getFullYear()); });
    // In-page anchors respect the sticky header and Horizon's scroll container
    $$('a[href^="#"], a[href^="/#"]', root).forEach(function (a) {
      if (a.dataset.hzAnchorDone) return;
      a.dataset.hzAnchorDone = '1';
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        var hash = href.slice(href.indexOf('#') + 1);
        if (!hash) return;
        if (href.charAt(0) === '/' && w.location.pathname !== '/') return;
        var target = d.getElementById(hash);
        if (!target) return;
        e.preventDefault();
        var offset = (parseFloat(getComputedStyle(html).getPropertyValue('--hz-header-h')) || 76) + 12;
        var y = scrollY() + target.getBoundingClientRect().top - offset;
        scrollToY(y, true);
        if (history.replaceState) history.replaceState(null, '', '#' + hash);
      });
    });
    // "Shop now" links: scroll to this page's purchase section when there is one, otherwise follow the link
    $$('[data-hz-buy-link]', root).forEach(function (a) {
      if (a.dataset.hzBuyDone) return;
      a.dataset.hzBuyDone = '1';
      a.addEventListener('click', function (e) {
        var target = d.getElementById('buy');
        if (!target) return;
        e.preventDefault();
        var offset = (parseFloat(getComputedStyle(html).getPropertyValue('--hz-header-h')) || 76) + 12;
        scrollToY(scrollY() + target.getBoundingClientRect().top - offset, true);
      });
    });
  }

  /* ------------------------------------------------------------ boot */
  function init(root) {
    root = root || d;
    initReveal(root);
    initParallax(root);
    initScrub(root);
    initSteps(root);
    initDrag(root);
    initFilm(root);
    initAccordions(root);
    initPointer(root);
    initSignup(root);
    initMisc(root);
    initHero(root);
    initBuyGallery(root);
    request();
  }

  function boot() {
    bindScroll();
    initHeader();
    initDrawerScrim();
    init(d);
    initStickyBar();
    // Hero intro
    $$('.hz-hero').forEach(function (h) {
      w.requestAnimationFrame(function () { h.classList.add('is-loaded'); });
    });
    // Re-measure split lines once fonts are ready
    if (d.fonts && d.fonts.ready) {
      d.fonts.ready.then(function () {
        $$('[data-hz-split]').forEach(measureLines);
        request();
      });
    }
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  d.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    initHeader();
    initStickyBar();
    $$('.hz-hero', e.target).forEach(function (h) { h.classList.add('is-loaded'); });
  });

  w.Hazemor = { init: init, scrollY: scrollY, onScroll: onScroll };
})();
